<template>
  <form
      @submit.prevent="updateGuest"
      autocomplete="off"
      class="w-full max-h-screen bg-background flex flex-col relative"
      :class="{ 'overflow-y-hidden': isBusy }"
  >
    <div v-show="isBusy" class="absolute inset-0 bg-faint-white flex items-center justify-center z-10">
      <spinner :color="'text-black'" :size="10" />
    </div>

    <page-header :title="pageTitle" back-to="guest">
      <AppButton color="blue" label="Save Changes" class="ml-2" type="submit" :icon="true">
        <template #icon>
          <Icon iconType="save" class="mr-2" />
        </template>
      </AppButton>
    </page-header>

    <main class="p-6 bg-background flex-1 overflow-y-scroll">
      <div class="w-full bg-white mt-6 p-6 rounded shadow-md">
        <div class="flex items-center mb-6">
          <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">Guest Information</h3>
          <hr class="flex-1 border-primary ml-4" />
        </div>

        <div class="grid grid-cols-12 gap-6">
          <div class="col-span-12 md:col-span-6 lg:col-span-4">
            <label class="field-label">First Name <span class="req">*</span></label>
            <input v-model="guest.first_name" class="bge-input bge-input-spacing rounded w-full" type="text" />
          </div>
          <div class="col-span-12 md:col-span-6 lg:col-span-4">
            <label class="field-label">Middle Name</label>
            <input v-model="guest.middle_name" class="bge-input bge-input-spacing rounded w-full" type="text" />
          </div>
          <div class="col-span-12 md:col-span-6 lg:col-span-4">
            <label class="field-label">Last Name <span class="req">*</span></label>
            <input v-model="guest.last_name" class="bge-input bge-input-spacing rounded w-full" type="text" />
          </div>
          <div class="col-span-12 md:col-span-6 lg:col-span-4">
            <label class="field-label">Company Name <span class="req">*</span></label>
            <input v-model="guest.company_name" class="bge-input bge-input-spacing rounded w-full" type="text" />
          </div>
          <div class="col-span-12 md:col-span-6 lg:col-span-4">
            <label class="field-label">Job Title</label>
            <input v-model="guest.job_title" class="bge-input bge-input-spacing rounded w-full" type="text" />
          </div>
          <div class="col-span-12 md:col-span-6 lg:col-span-4">
            <label class="field-label">Department</label>
            <input v-model="guest.department" class="bge-input bge-input-spacing rounded w-full" type="text" />
          </div>
          <div class="col-span-12 md:col-span-6 lg:col-span-4">
            <label class="field-label">Email</label>
            <input v-model="guest.email" class="bge-input bge-input-spacing rounded w-full" type="email" />
          </div>
          <div class="col-span-12 md:col-span-6 lg:col-span-4">
            <label class="field-label">Telephone</label>
            <input v-model="guest.telephone" class="bge-input bge-input-spacing rounded w-full" type="text" />
          </div>
          <div class="col-span-12 md:col-span-6 lg:col-span-4">
            <label class="field-label">Mobile</label>
            <input v-model="guest.mobile" class="bge-input bge-input-spacing rounded w-full" type="text" placeholder="+44 7XXXXXXXXX or +1 555 555 5555" />
          </div>
        </div>
      </div>

      <div class="w-full bg-white mt-6 p-6 rounded shadow-md">
        <div class="flex items-center mb-6">
          <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">Address Details</h3>
          <hr class="flex-1 border-primary ml-4" />
        </div>

        <div class="flex items-center mb-4 space-x-4">
          <span class="mr-1 font-semibold uppercase text-xs tracking-wide">Use UK Postcode Lookup</span>
          <InputSwitch v-model="useUkLookup" @change="onModeSwitched" />
          <span class="text-xs text-gray-500">Switching clears all address fields</span>
        </div>

        <div class="grid grid-cols-12 gap-6">
          <template v-if="useUkLookup">
            <div class="col-span-12 md:col-span-6 lg:col-span-4">
              <label class="field-label">Postcode <span class="req">*</span></label>
              <div class="relative">
                <div class="flex">
                  <input class="bge-input bge-input-spacing rounded flex-1" type="text" v-model="guest.postcode" placeholder="e.g. SW1A 1AA" />
                  <button
                      type="button"
                      @click="findAddress"
                      class="ml-3 relative overflow-hidden flex items-center bg-green-200 border hover:bg-green-300 border-green-400 hover:border-green-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-3 py-1"
                  >
                    <svg class="stroke-current h-5 w-5 mr-2" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <span>Find</span>
                    <div v-if="selectAddressState === 'Loading'" class="z-10 bg-green-200 absolute inset-0 flex items-center justify-center">
                      <spinner :color="'text-secondary'" :size="5" />
                    </div>
                  </button>
                </div>

                <div
                    v-if="selectAddressState === 'Results'"
                    class="absolute left-0 right-0 mt-2 bg-white shadow-2xl rounded-lg z-30 p-3"
                >
                  <div class="text-[10px] uppercase tracking-wide text-gray-500 px-2 py-1">{{ availableAddressData && availableAddressData.postcode }}</div>
                  <ul class="max-h-64 overflow-y-auto border rounded">
                    <li
                        v-for="(address, i) in (availableAddressData && availableAddressData.addresses ? availableAddressData.addresses : [])"
                        :key="i"
                    >
                      <button
                          type="button"
                          @click="applySelected(address)"
                          class="w-full text-left px-4 py-3 hover:bg-blue-50 focus:bg-blue-50 transition"
                      >
                        <div class="text-sm">{{ address.formatted_address }}</div>
                        <div class="text-xs text-gray-500">
                          {{ address.line_1 }}<template v-if="address.line_2">, {{ address.line_2 }}</template><template v-if="address.line_3">, {{ address.line_3 }}</template>
                        </div>
                      </button>
                    </li>
                  </ul>
                  <div class="flex justify-end mt-3">
                    <button
                        @click="closeResults"
                        type="button"
                        class="inline-flex items-center bg-gray-200 border hover:bg-gray-300 border-gray-400 hover:border-gray-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-3 py-1"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-span-12 md:col-span-6 lg:col-span-4">
              <label class="field-label">Address 1</label>
              <input class="bge-input bge-input-spacing rounded w-full" type="text" v-model="guest.address1" />
            </div>

            <div class="col-span-12 md:col-span-6 lg:col-span-4">
              <label class="field-label">Address 2</label>
              <input class="bge-input bge-input-spacing rounded w-full" type="text" v-model="guest.address2" />
            </div>
          </template>

          <template v-else>
            <div class="col-span-12 md:col-span-6 lg:col-span-4">
              <label class="field-label">Address 1</label>
              <input class="bge-input bge-input-spacing rounded w-full" type="text" v-model="guest.address1" placeholder="Street and number" />
            </div>

            <div class="col-span-12 md:col-span-6 lg:col-span-4">
              <label class="field-label">Address 2</label>
              <input class="bge-input bge-input-spacing rounded w-full" type="text" v-model="guest.address2" placeholder="Apartment, suite, etc. (optional)" />
            </div>

            <div class="col-span-12 md:col-span-6 lg:col-span-4">
              <label class="field-label">Postal Code</label>
              <input class="bge-input bge-input-spacing rounded w-full" type="text" v-model="guest.postcode" placeholder="ZIP / Postal Code" />
            </div>
          </template>

          <div class="col-span-12 md:col-span-6 lg:col-span-4">
            <label class="field-label">Address 3</label>
            <input class="bge-input bge-input-spacing rounded w-full" type="text" v-model="guest.address3" />
          </div>

          <div class="col-span-12 md:col-span-6 lg:col-span-4">
            <label class="field-label">City</label>
            <input class="bge-input bge-input-spacing rounded w-full" type="text" v-model="guest.city" />
          </div>

          <div class="col-span-12 md:col-span-6 lg:col-span-4">
            <label class="field-label">County / State / Region</label>
            <input class="bge-input bge-input-spacing rounded w-full" type="text" v-model="guest.county" />
          </div>

          <div class="col-span-12 md:col-span-6 lg:col-span-4">
            <label class="field-label">Country</label>
            <select v-model="guest.country" class="bge-input bge-select rounded w-full">
              <optgroup label="Frequently Used">
                <option value="United Kingdom">United Kingdom</option>
                <option value="Kenya">Kenya</option>
                <option value="England">England</option>
                <option value="Scotland">Scotland</option>
                <option value="Wales">Wales</option>
                <option value="Northern Ireland">Northern Ireland</option>
                <option value="Republic of Ireland">Republic of Ireland</option>
              </optgroup>
              <optgroup label="All Countries">
                <template v-for="(country, i) in countries">
                  <option :key="i" :value="country">{{ country }}</option>
                </template>
              </optgroup>
            </select>
          </div>
        </div>
      </div>

      <div class="w-full bg-white mt-6 p-6 rounded shadow-md">
        <div class="flex items-center mb-6">
          <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">Notes</h3>
          <hr class="flex-1 border-primary ml-4" />
        </div>

        <div class="grid grid-cols-12 gap-6">
          <div class="col-span-12">
            <label class="field-label">Additional Notes</label>
            <textarea v-model="guest.notes" class="bge-input bge-input-spacing rounded w-full" rows="5" placeholder="Enter any additional notes about the guest..."></textarea>
          </div>
        </div>
      </div>
    </main>

    <div
        v-if="selectAddressState === 'Results'"
        class="fixed inset-0 z-20"
        @click="closeResults"
    />
  </form>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import Spinner from "@/components/Spinner.vue";
import AppButton from "@/components/Button.vue";
import Icon from "@/components/Icon.vue";
import notificationMixin from "@/mixins/notificationMixin";
import { COUNTRIES } from "@/utils/constants";
import InputSwitch from "primevue/inputswitch";

export default {
  name: "EditGuest",
  mixins: [notificationMixin],
  components: {
    PageHeader,
    Spinner,
    AppButton,
    Icon,
    InputSwitch
  },
  data() {
    return {
      isBusy: false,
      pageTitle: "Update Guest",
      guest_id: null,
      guest: {},
      countries: [],
      selectAddressState: "Idle",
      availableAddressData: {},
      selectedAddress: null,
      useUkLookup: true
    };
  },
  async mounted() {
    this.countries = COUNTRIES;
    this.guest_id = this.$route.params.guest_id;
    if (this.guest_id) {
      await this.loadGuestDetails();
      this.syncLookupMode();
    } else {
      this.notifyWarning("Guest ID missing");
      await this.$router.push({ name: "guest" });
    }
  },
  methods: {
    onModeSwitched() {
      this.clearAddressFields();
    },
    closeResults() {
      this.selectAddressState = "Idle";
      this.selectedAddress = null;
    },
    clearAddressFields() {
      this.guest.postcode = "";
      this.guest.address1 = "";
      this.guest.address2 = "";
      this.guest.address3 = "";
      this.guest.city = "";
      this.guest.county = "";
      this.guest.country = "";
      this.selectAddressState = "Idle";
      this.availableAddressData = {};
      this.selectedAddress = null;
    },
    async loadGuestDetails() {
      this.isBusy = true;
      try {
        const response = await this.GuestService.getGuestById(this.guest_id);
        this.guest = response ? response : {};
        this.syncLookupMode();
      } catch (err) {
        this.notifyError("Failed to load guest details");
      } finally {
        this.isBusy = false;
      }
    },
    async findAddress() {
      try {
        if (!this.guest.postcode) {
          this.notifyWarning("Please enter a postcode first");
          return;
        }
        this.selectAddressState = "Loading";
        const result = await this.SalesProspectService.findAddressFromPostcode(this.guest.postcode);
        this.availableAddressData = result.data;
        this.selectAddressState = "Results";
      } catch (error) {
        this.selectAddressState = "Idle";
        let msg;
        if (error && error.status === 404) {
          msg = "No addresses found for this postcode.";
        } else if (error && error.status === 400) {
          msg = "Invalid postcode.";
        } else if (error && error.status === 429) {
          msg = "Too many requests. Please try again shortly.";
        } else {
          msg = "Failed to fetch addresses.";
        }
        this.notifyError(msg);
      }
    },
    applySelected(address) {
      if (!address) return;
      this.guest.address1 = address.line_1 ? address.line_1 : "";
      this.guest.address2 = address.line_2 ? address.line_2 : "";
      this.guest.address3 = address.line_3 ? address.line_3 : "";
      this.guest.city = address.town_or_city ? address.town_or_city : "";
      this.guest.county = address.county ? address.county : "";
      this.guest.country = address.country ? address.country : this.guest.country;
      this.selectAddressState = "Idle";
      this.selectedAddress = null;
    },
    async updateGuest() {
      if (!this.validateGuestFields()) return;
      this.isBusy = true;
      try {
        await this.GuestService.updateGuest(this.guest_id, this.guest);
        this.notifySuccess("Guest updated successfully.");
        await this.$router.push({ name: "guest" });
      } catch (error) {
        this.notifyError("Failed to update guest.");
      } finally {
        this.isBusy = false;
      }
    },
    validateGuestFields() {
      const g = this.guest;
      if (!g.first_name) return this.notifyWarning("Please enter First Name.");
      if (!g.last_name) return this.notifyWarning("Please enter Last Name.");
      if (!g.company_name) return this.notifyWarning("Please enter Company Name.");
      if (g.email && this.$validator && this.$validator.isEmail && !this.$validator.isEmail(g.email)) {
        return this.notifyWarning("Please enter a valid Email address.");
      }
      if (g.mobile && this.$validator && this.$validator.isMobilePhone && !this.$validator.isMobilePhone(g.mobile, "any")) {
        return this.notifyWarning("Please enter a valid mobile number.");
      }
      if (this.useUkLookup) {
        if (!g.postcode) return this.notifyWarning("Please enter a UK Postcode.");
        if (this.$validator && this.$validator.isPostalCode && !this.$validator.isPostalCode(g.postcode, "GB")) {
          return this.notifyWarning("Please enter a valid UK Postcode (e.g. SW1A 1AA).");
        }
      }
      return true;
    },

    syncLookupMode() {
      if (this.guest && this.guest.country) {
        this.useUkLookup = this.isUKCountry(this.guest.country);
      }
    },
    isUKCountry(country) {
      if (!country) return false;
      const c = String(country).trim().toLowerCase();
      return [
        "united kingdom",
        "uk",
        "england",
        "scotland",
        "wales",
        "northern ireland"
      ].includes(c);
    }


  }
};
</script>

<style scoped>
.field-label {
  @apply uppercase font-semibold text-sm;
}
.req {
  @apply text-red-400;
}
textarea {
  resize: none;
}
</style>
